import { CommonModule } from '@angular/common';
import { Component, OnDestroy, inject, type OnInit } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAX_MULTI_VALUE_FILTER_VALUES } from '@cdw-ae/common-data';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { CdwSlideToggleModule } from '@cdw-ae/common-ui-components-slide-toggle';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';
import {
  BaseMatcherEditorComponent,
  type MatcherComponentRegistryService,
} from '@cdw-ae/filter-bar-core';
import { MultiTextSearchMatcher } from '@cdw-ae/filter-bar-matcher-configs';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, finalize, map, startWith, tap } from 'rxjs/operators';

export interface MultiTextSearchValue {
  mode: 'contains' | 'startsWith';
  values: string[];
  /**
   * Validation outcome, persisted so reopening a filter chip restores the
   * summary callout without a repeat HTTP call.
   *
   * Three-tier classification (EDUS-2662/2671):
   * - exact: case-insensitive exact matches
   * - partial: prefix matches (e.g., "SN-1" matches "SN-123")
   * - notFound: no matches at all
   * - counts: database record counts for display (partial can match multiple records)
   * - duplicatesRemoved: count of duplicate values removed on frontend before validation
   */
  exact?: string[];
  partial?: string[];
  notFound?: string[];
  counts?: {
    exact: number;
    partial: number;
    notFound: number;
  };
  duplicatesRemoved?: number;
}

/** Narrows an untrusted parsed `matchValue` field to a string array. */
function toStringArray(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((v): v is string => typeof v === 'string')
    : [];
}

@Component({
  selector: 'cdw-multi-text-search-matcher',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CdwButtonModule,
    CdwIconModule,
    CdwSlideToggleModule,
    CommonUiComponentsTextInputModule,
  ],
  template: `
    <section [formGroup]="state">
      <div
        *ngIf="capWarning"
        class="cap-warning"
        role="alert"
        data-testid="multi-text-search-cap-warning"
      >
        {{ capWarning }}
      </div>
      <div class="toggle-row">
        <lib-cdw-slide-toggle
          formControlName="pasteMode"
          label="Paste a List"
          [showCheckmark]="true"
          trackColor="var(--color-tertiary-blue-toggle-track-bg)"
          checkmarkColor="var(--color-tertiary-blue-deep)"
          minusIconColor="var(--color-primary-gray-500)"
        ></lib-cdw-slide-toggle>
      </div>

      <!-- Multi-contains UI (toggle OFF) -->
      <ng-container *ngIf="!pasteMode">
        <div formArrayName="values">
          <ng-container
            *ngFor="let control of valuesArray.controls; let i = index; let last = last"
          >
            <span class="contains-label">Contains</span>
            <div class="value-row">
              <lib-common-ui-components-text-input
                [formControlName]="i"
                size="100%"
                [attr.data-testid]="'multi-text-search-input-' + i"
              ></lib-common-ui-components-text-input>
              <div
                class="remove-action"
                [class.hidden]="valuesArray.length <= 1"
                (click)="removeRow(i)"
                (keydown.enter)="removeRow(i)"
                tabindex="0"
                role="button"
                aria-label="Remove value"
              >
                <lib-cdw-icon icon="delete" size="20px" customColor="var(--color-tertiary-blue-cta)"></lib-cdw-icon>
                <span class="remove-text">remove</span>
              </div>
            </div>
            <div *ngIf="!last" class="or-divider">
              <span class="or-text">OR</span>
            </div>
          </ng-container>
        </div>
        <lib-cdw-button
          *ngIf="valuesArray.length < MAX_ROWS"
          mode="basic"
          level="primary"
          [disabled]="!lastRowHasValue"
          (buttonClick)="addRow()"
        >
          + Add another filter
        </lib-cdw-button>
      </ng-container>

      <!-- Paste UI (toggle ON) -->
      <ng-container *ngIf="pasteMode">
        <p class="instruction-text">
          Type or paste each {{ matcherOptions.field.displayName.toLowerCase() || 'value' }} on a new line
        </p>
        <span class="starts-with-label">Starts With</span>
        <div class="paste-input-wrapper">
          <lib-common-ui-components-text-input
            formControlName="textAreaControl"
            [textArea]="true"
            [textAreaMinRows]="5"
            [textAreaMaxRows]="12"
            size="100%"
            data-testid="multi-text-search-paste-input"
          ></lib-common-ui-components-text-input>
        </div>
        <div *ngIf="exactValues.length > 0 || partialValues.length > 0 || notFoundValues.length > 0 || duplicatesRemoved > 0" class="validation-summary" data-testid="validation-summary">
          <div class="validation-label">Validation results:</div>
          <div class="validation-content">
            <span class="validation-counts" data-testid="validation-counts">
              <!-- Gate each segment on its own count, not on the value array. A pasted
                   value can be classified PARTIAL while contributing no DB value of its
                   own (paste ["LAP-100", "LAP-1000"] vs DB ["LAP-1000"]: the single DB
                   value is attributed to exact), and gating on the array would render a
                   literal "0 partial". -->
              <span *ngIf="exactCount > 0" class="exact-count" data-testid="exact-count">{{ exactCount }} exact</span>
              <span *ngIf="exactCount > 0 && partialCount > 0" class="separator">·</span>
              <span *ngIf="partialCount > 0" class="partial-count" data-testid="partial-count">{{ partialCount }} partial</span>
              <span *ngIf="(exactCount > 0 || partialCount > 0) && notFoundCount > 0" class="separator">·</span>
              <span *ngIf="notFoundCount > 0" class="not-found-count" data-testid="not-found-count">{{ notFoundCount }} not found</span>
            </span>
            <button *ngIf="notFoundValues.length > 0" type="button" class="copy-errors-btn" data-testid="copy-errors-btn" (click)="copyNotFound()">
              Copy Failed <lib-cdw-icon icon="content_copy" size="12px"></lib-cdw-icon>
            </button>
          </div>
          <div *ngIf="duplicatesRemoved > 0" class="duplicates-info" data-testid="duplicates-info">
            {{ duplicatesRemoved }} duplicate{{ duplicatesRemoved > 1 ? 's' : '' }} removed
          </div>
        </div>
      </ng-container>
    </section>
  `,
  styleUrls: ['./multi-text-search.matcher.scss'],
})
export class MultiTextSearchMatcherComponent
  extends BaseMatcherEditorComponent
  implements OnInit, OnDestroy
{
  readonly MAX_ROWS = 5;

  public override state = new FormGroup({
    pasteMode: new FormControl<boolean>(false, { nonNullable: true }),
    values: new FormArray<FormControl<string>>([]),
    textAreaControl: new FormControl<string>('', {
      nonNullable: true,
    }),
  });

  public get pasteMode(): boolean {
    return this.state.controls.pasteMode.value;
  }

  public get valuesArray(): FormArray<FormControl<string>> {
    return this.state.controls.values;
  }

  public get lastRowHasValue(): boolean {
    if (this.valuesArray.length === 0) {
      return false;
    }
    const last = this.valuesArray.at(this.valuesArray.length - 1);
    return !!last.value?.trim();
  }

  /**
   * Persistent over-cap message shown in the editor while the current input
   * exceeds the cap (null when within it). Replaces the transient snackbar so
   * the warning stays visible as long as it's relevant (EDUS-2575).
   */
  protected capWarning: string | null = null;

  protected exactValues: string[] = [];
  protected partialValues: string[] = [];
  protected notFoundValues: string[] = [];

  protected exactCount = 0;
  protected partialCount = 0;
  protected notFoundCount = 0;

  protected duplicatesRemoved = 0;
  private validatedTextareaValue: string | null = null;
  private readonly snackbar = inject(SnackbarService);
  private readonly destroy$ = new Subject<void>();

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor() {
    super();
    this.matcherDisplayName = MultiTextSearchMatcher.displayName;
    this.matcherName = MultiTextSearchMatcher.name;
  }

  public ngOnInit(): void {
    this.restoreFromMatchValue();

    // Ensure at least one row exists when switching to contains mode
    this.state.controls.pasteMode.valueChanges.subscribe((isPaste) => {
      if (!isPaste && this.valuesArray.length === 0) {
        this.addRow();
      }
    });

    // Re-emit whenever toggle, text inputs, or textarea change
    this.state.valueChanges
      .pipe(startWith(this.state.value))
      .subscribe(() => this.emitValue());

    this.hasErrors$ = this.state.valueChanges.pipe(
      map(() => this.hasValidationErrors()),
      startWith(this.hasValidationErrors())
    );
  }

  public addRow(value = ''): void {
    this.valuesArray.push(
      new FormControl<string>(value, { nonNullable: true })
    );
  }

  public removeRow(index: number): void {
    this.valuesArray.removeAt(index);
  }

  private restoreFromMatchValue(): void {
    const matchValue = this.matcherOptions.matchValue;

    if (matchValue && typeof matchValue === 'string') {
      try {
        const parsed = JSON.parse(matchValue);

        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          // Structured format: {"mode":"startsWith","values":["..."]}
          const { mode, values } = parsed as MultiTextSearchValue;

          if (mode === 'startsWith') {
            this.state.controls.pasteMode.setValue(true);
            const restoredText = (values ?? []).join('\n');
            this.state.controls.textAreaControl.setValue(restoredText);

            const parsedValue = parsed as MultiTextSearchValue;
            this.exactValues = toStringArray(parsedValue.exact);
            this.partialValues = toStringArray(parsedValue.partial);
            this.notFoundValues = toStringArray(parsedValue.notFound);
            this.exactCount = parsedValue.counts?.exact ?? 0;
            this.partialCount = parsedValue.counts?.partial ?? 0;
            this.notFoundCount = parsedValue.counts?.notFound ?? 0;
            this.duplicatesRemoved = parsedValue.duplicatesRemoved ?? 0;

            // Marks the restored text as already-validated so the next edit
            // trips the stale-check in emitValue(). Gated on any array —
            // an all-exact paste persists `exact` only, and would
            // otherwise keep showing "N exact" after the user edits.
            if (this.exactValues.length > 0 || this.partialValues.length > 0 || this.notFoundValues.length > 0) {
              this.validatedTextareaValue = restoredText;
            }
          } else {
            this.state.controls.pasteMode.setValue(false);
            (values ?? []).forEach((v: string) => this.addRow(v));
          }
        } else if (Array.isArray(parsed)) {
          // Legacy plain array format — treat as contains
          parsed.forEach((v: string) => this.addRow(v));
        } else {
          this.addRow(matchValue);
        }
      } catch {
        this.addRow(matchValue);
      }
    } else {
      this.addRow();
    }

    // If no rows were added for contains mode, ensure at least one
    if (!this.pasteMode && this.valuesArray.length === 0) {
      this.addRow();
    }
  }

  private emitValue(): void {
    if (this.pasteMode) {
      const text = this.state.controls.textAreaControl.value;

      if (this.validatedTextareaValue !== null && text !== this.validatedTextareaValue) {
        this.exactValues = [];
        this.partialValues = [];
        this.notFoundValues = [];
        this.exactCount = 0;
        this.partialCount = 0;
        this.notFoundCount = 0;
        this.validatedTextareaValue = null;
      }

      const capped = this.getParsedCappedValues();
      const payload: MultiTextSearchValue = { mode: 'startsWith', values: capped };
      // Persisted as a trio — see MultiTextSearchValue. Restoring only some
      // arrays renders wrong counts in the summary callout.
      if (this.exactValues.length > 0) {
        payload.exact = this.exactValues;
      }
      if (this.partialValues.length > 0) {
        payload.partial = this.partialValues;
      }
      if (this.notFoundValues.length > 0) {
        payload.notFound = this.notFoundValues;
      }
      if (this.exactCount > 0 || this.partialCount > 0 || this.notFoundCount > 0) {
        payload.counts = {
          exact: this.exactCount,
          partial: this.partialCount,
          notFound: this.notFoundCount,
        };
      }
      if (this.duplicatesRemoved > 0) {
        payload.duplicatesRemoved = this.duplicatesRemoved;
      }
      this.valueToMatch = capped.length > 0 ? JSON.stringify(payload) : null;
    } else {
      const values = this.valuesArray.value.filter((v) => v?.trim());
      const capped = this.enforceCap(values);
      this.valueToMatch =
        capped.length > 0
          ? JSON.stringify({ mode: 'contains', values: capped })
          : null;
    }
  }

  /**
   * Caps the applied value list at {@link MAX_MULTI_VALUE_FILTER_VALUES} (the
   * shared front/back cap). The textarea is left intact (non-destructive) — only
   * the first N are applied — and {@link capWarning} surfaces a persistent,
   * explicit message in the editor while the input is over the cap (EDUS-2575).
   */
  private enforceCap(values: string[]): string[] {
    if (values.length > MAX_MULTI_VALUE_FILTER_VALUES) {
      const excess = values.length - MAX_MULTI_VALUE_FILTER_VALUES;
      this.capWarning =
        `Pasted ${values.length.toLocaleString()} values — only the first ` +
        `${MAX_MULTI_VALUE_FILTER_VALUES.toLocaleString()} will be applied ` +
        `(${excess.toLocaleString()} not applied).`;
      return values.slice(0, MAX_MULTI_VALUE_FILTER_VALUES);
    }
    this.capWarning = null;
    return values;
  }

  private getParsedCappedValues(): string[] {
    const text = this.state.controls.textAreaControl.value;
    const lines = text
      .split(/\r?\n/)
      .map((line) =>
        line
          .replace(/\t|\u00A0|\u200B|\u200C|\u200D|\uFEFF/g, ' ')
          .trim()
      )
      .filter((line) => line.length > 0);

    const seen = new Set<string>();
    const unique = lines.filter((line) => {
      const key = line.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return this.enforceCap(unique);
  }

  private hasValidationErrors(): boolean {
    if (this.pasteMode) {
      const text = this.state.controls.textAreaControl.value;
      return !text || text.trim().length === 0;
    }
    return (
      this.valuesArray.length === 0 ||
      this.valuesArray.value.some((v) => !v?.trim())
    );
  }

  public override validate$ = (): Observable<void> => {
    const validateFn = this.matcherOptions.selectedMatcher.options?.validateFn;
    if (!(this.pasteMode && validateFn)) {
      return of(undefined);
    }

    const currentText = this.state.controls.textAreaControl.value;
    const capped = this.getParsedCappedValues();

    // Calculate duplicates removed on frontend (deduplication happens before backend call)
    const allLines = currentText
      .split(/\r?\n/)
      .map((line) =>
        line
          .replace(/\t| |​|‌|‍|﻿/g, ' ')
          .trim()
      )
      .filter((line) => line.length > 0);
    const duplicatesRemovedCount = allLines.length - capped.length;

    this.state.controls.textAreaControl.disable({ emitEvent: false });
    return validateFn(capped).pipe(
      tap(({ exact, partial, notFound, counts }) => {
        this.exactValues = exact;
        this.partialValues = partial;
        this.notFoundValues = notFound;
        this.exactCount = counts.exact;
        this.partialCount = counts.partial;
        this.notFoundCount = counts.notFound;
        this.duplicatesRemoved = duplicatesRemovedCount;
        this.validatedTextareaValue = currentText;
        this.emitValue();
      }),
      map(() => undefined),
      catchError((err) => {
        this.snackbar.showError('Unable to validate filter values. Please try again.');
        return throwError(() => err);
      }),
      finalize(() => {
        if (!this.destroy$.closed) {
          this.state.controls.textAreaControl.enable({ emitEvent: false });
        }
      })
    );
  };

  public copyNotFound(): void {
    navigator.clipboard.writeText(this.notFoundValues.join('\n')).catch(() => {
      this.snackbar.showError('Unable to copy to clipboard.');
    });
  }

  public static registerMultiTextSearch(
    registry: MatcherComponentRegistryService
  ): void {
    registry.registerComponent(
      'multiTextSearch',
      MultiTextSearchMatcherComponent
    );
  }
}
